import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Badge } from '../ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { useMenuSettings } from '../../contexts/MenuSettingsContext'
import { purchasingAPI, type POLineSchedule } from '../../lib/api'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts'
import { TabProps } from './types'
import { useFormatters } from '../../contexts/PreferencesContext'
import { ChartWrapper } from './ChartWrapper'
import { Clock, AlertTriangle, CheckCircle, TrendingUp, Package } from 'lucide-react'
import api from '../../lib/api'

// Status colors for expediting
const STATUS_COLORS = {
  onTime: '#10b981',
  atRisk: '#f59e0b',
  late: '#ef4444',
  expedited: '#3b82f6',
}

interface ExpediteStatus {
  status: string
  count: number
  value: number
  color: string
  [key: string]: string | number  // Index signature for Recharts compatibility
}

interface ExpediteBySupplier {
  supplier: string
  onTime: number
  atRisk: number
  late: number
  expedited: number
}

function todayISO(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

function daysLate(scheduleDate: string): number {
  const d = new Date(scheduleDate)
  const now = new Date()
  return Math.floor((now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24))
}

function daysLateClass(days: number): string {
  if (days > 14) return 'text-red-600 font-semibold'
  if (days > 7) return 'text-orange-600 font-medium'
  return ''
}

export function ExpeditingTab({ data: _data, currency: _currency, filters: _filters }: TabProps) {
  const { formatCurrency, formatNumber } = useFormatters()
  const { isFeatureEnabled } = useMenuSettings()
  const scheduleCollabEnabled = isFeatureEnabled('schedule_collaboration')
  const [expediteData, setExpediteData] = useState<ExpediteStatus[]>([])
  const [supplierData, setSupplierData] = useState<ExpediteBySupplier[]>([])
  const [trendData, setTrendData] = useState<Array<{ month: string; expedited: number; late: number; atRisk: number }>>([])
  const [loading, setLoading] = useState(true)
  const [pastDueSchedules, setPastDueSchedules] = useState<POLineSchedule[]>([])
  const [schedulesLoading, setSchedulesLoading] = useState(false)

  useEffect(() => {
    if (!scheduleCollabEnabled) return
    let cancelled = false
    const fetchPastDue = async () => {
      try {
        setSchedulesLoading(true)
        const res = await purchasingAPI.getSchedules({
          schedule_date__lt: todayISO(),
          status__in: 'scheduled,partially_shipped',
          page_size: 50,
        })
        if (cancelled) return
        setPastDueSchedules(res.data.results || [])
      } catch (err) {
        console.error('Error fetching past-due schedules:', err)
        if (!cancelled) setPastDueSchedules([])
      } finally {
        if (!cancelled) setSchedulesLoading(false)
      }
    }
    fetchPastDue()
    return () => {
      cancelled = true
    }
  }, [scheduleCollabEnabled])

  useEffect(() => {
    const fetchExpediteData = async () => {
      try {
        setLoading(true)
        // Fetch PO lines data for expediting analysis
        const response = await api.get('/purchasing/lines/', {
          params: { page_size: 500 }
        })
        const lines = response.data.results || response.data || []

        // Categorize lines by delivery status
        const statusCounts: Record<string, { count: number; value: number }> = {
          onTime: { count: 0, value: 0 },
          atRisk: { count: 0, value: 0 },
          late: { count: 0, value: 0 },
          expedited: { count: 0, value: 0 },
        }

        const supplierStats: Record<string, { onTime: number; atRisk: number; late: number; expedited: number }> = {}

        const today = new Date()
        lines.forEach((line: Record<string, unknown>) => {
          const requiredDate = (line['required_date'] || line['delivery_date']) ? new Date((line['required_date'] || line['delivery_date']) as string) : null
          const value = parseFloat(String(line['line_value'] || line['total_value'] || 0))
          const supplierName = (line['supplier_name'] as string) || 'Unknown'

          if (!supplierStats[supplierName]) {
            supplierStats[supplierName] = { onTime: 0, atRisk: 0, late: 0, expedited: 0 }
          }

          // Determine status
          let status = 'onTime'
          if (line['status'] === 'complete' || line['status'] === 'delivered' || line['status'] === 'received') {
            status = 'onTime'
          } else if (requiredDate) {
            const daysUntilDue = Math.floor((requiredDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
            if (daysUntilDue < 0) {
              status = 'late'
            } else if (daysUntilDue <= 7) {
              status = 'atRisk'
            } else if (line['is_expedited'] || line['acknowledge_status'] === 'expedited') {
              status = 'expedited'
            }
          }

          statusCounts[status].count += 1
          statusCounts[status].value += value
          supplierStats[supplierName][status as keyof typeof supplierStats[string]] += 1
        })

        // Convert to array format
        const statusData = Object.entries(statusCounts).map(([status, data]) => ({
          status: status.charAt(0).toUpperCase() + status.slice(1).replace(/([A-Z])/g, ' $1'),
          count: data.count,
          value: data.value,
          color: STATUS_COLORS[status as keyof typeof STATUS_COLORS],
        }))
        setExpediteData(statusData)

        // Top suppliers by expedite needs
        const supplierArray = Object.entries(supplierStats)
          .map(([supplier, stats]) => ({
            supplier: supplier.length > 12 ? supplier.substring(0, 12) + '...' : supplier,
            ...stats,
          }))
          .sort((a, b) => (b.late + b.atRisk) - (a.late + a.atRisk))
          .slice(0, 8)
        setSupplierData(supplierArray)

        // No monthly trend data available yet
        setTrendData([])

      } catch (error) {
        console.error('Error fetching expedite data:', error)
        setExpediteData([])
        setSupplierData([])
        setTrendData([])
      } finally {
        setLoading(false)
      }
    }

    fetchExpediteData()
  }, [])

  const displayStatus = expediteData
  const displaySupplier = supplierData
  const displayTrend = trendData

  // Calculate totals
  const totalLines = displayStatus.reduce((sum, s) => sum + s.count, 0)
  const totalLate = displayStatus.find(s => s.status === 'Late')?.count || 0
  const totalAtRisk = displayStatus.find(s => s.status === 'At Risk')?.count || 0
  const totalExpedited = displayStatus.find(s => s.status === 'Expedited')?.count || 0
  const lateValue = displayStatus.find(s => s.status === 'Late')?.value || 0

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-lg shadow-sm p-4">
        <h2 className="text-lg font-semibold">Expediting Dashboard</h2>
        <p className="text-sm text-muted-foreground">
          Track and manage delivery expediting across purchase order lines
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card className="shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Lines</p>
                <p className="text-2xl font-bold">{formatNumber(totalLines)}</p>
              </div>
              <Package className="h-8 w-8 text-gray-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">On Time</p>
                <p className="text-2xl font-bold text-green-600">
                  {totalLines > 0 ? ((totalLines - totalLate - totalAtRisk) / totalLines * 100).toFixed(1) : '0.0'}%
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">At Risk</p>
                <p className="text-2xl font-bold text-amber-600">{totalAtRisk}</p>
              </div>
              <Clock className="h-8 w-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-red-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Late</p>
                <p className="text-2xl font-bold text-red-600">{totalLate}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Expedited</p>
                <p className="text-2xl font-bold text-blue-600">{totalExpedited}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Status Distribution Pie */}
        <ChartWrapper
          title="Delivery Status Distribution"
          data={displayStatus}
          exportFilename="expedite-status-distribution"
        >
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={displayStatus.filter(d => d.count > 0)}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                dataKey="count"
                nameKey="status"
                label={false}
              >
                {displayStatus.filter(d => d.count > 0).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip formatter={(value: number, name: string) => [value, name]} />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-x-4 gap-y-1 mt-2">
            {displayStatus.filter(d => d.count > 0).map((entry) => (
              <div key={entry.status} className="flex items-center gap-1.5 text-sm">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                <span>{entry.status}: {entry.count}</span>
              </div>
            ))}
          </div>
        </ChartWrapper>

        {/* Expediting Trend */}
        <ChartWrapper
          title="Expediting Trend"
          data={displayTrend}
          exportFilename="expedite-trend"
          legend={[
            { color: STATUS_COLORS.expedited, label: 'Expedited' },
            { color: STATUS_COLORS.late, label: 'Late' },
            { color: STATUS_COLORS.atRisk, label: 'At Risk' },
          ]}
        >
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={displayTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Line type="monotone" dataKey="expedited" stroke={STATUS_COLORS.expedited} strokeWidth={2} name="Expedited" />
              <Line type="monotone" dataKey="late" stroke={STATUS_COLORS.late} strokeWidth={2} name="Late" />
              <Line type="monotone" dataKey="atRisk" stroke={STATUS_COLORS.atRisk} strokeWidth={2} name="At Risk" />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      {/* Supplier Expediting Status */}
      <ChartWrapper
        title="Expediting Status by Supplier"
        data={displaySupplier}
        exportFilename="expedite-by-supplier"
        legend={[
          { color: STATUS_COLORS.onTime, label: 'On Time' },
          { color: STATUS_COLORS.atRisk, label: 'At Risk' },
          { color: STATUS_COLORS.late, label: 'Late' },
          { color: STATUS_COLORS.expedited, label: 'Expedited' },
        ]}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={displaySupplier} layout="vertical">
            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
            <XAxis type="number" tick={{ fontSize: 11 }} />
            <YAxis type="category" dataKey="supplier" tick={{ fontSize: 11 }} width={100} />
            <Tooltip />
            <Bar dataKey="onTime" stackId="a" fill={STATUS_COLORS.onTime} name="On Time" />
            <Bar dataKey="atRisk" stackId="a" fill={STATUS_COLORS.atRisk} name="At Risk" />
            <Bar dataKey="late" stackId="a" fill={STATUS_COLORS.late} name="Late" />
            <Bar dataKey="expedited" stackId="a" fill={STATUS_COLORS.expedited} name="Expedited" />
          </BarChart>
        </ResponsiveContainer>
      </ChartWrapper>

      {/* Value at Risk */}
      <Card className="shadow-sm bg-gradient-to-r from-red-50 to-amber-50 border-red-200">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground font-medium">Value at Risk (Late Deliveries)</p>
              <p className="text-3xl font-bold text-red-700">{formatCurrency(lateValue)}</p>
            </div>
            <AlertTriangle className="h-12 w-12 text-red-400" />
          </div>
        </CardContent>
      </Card>

      {/* Past-Due Schedule Lines (DE feature flag) */}
      {scheduleCollabEnabled && (
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">Past-Due Schedule Lines</CardTitle>
          </CardHeader>
          <CardContent>
            {schedulesLoading ? (
              <p className="text-sm text-muted-foreground">Loading past-due schedules...</p>
            ) : pastDueSchedules.length === 0 ? (
              <p className="text-sm text-muted-foreground">No past-due schedules</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PO #</TableHead>
                      <TableHead>Line #</TableHead>
                      <TableHead>Sched Line #</TableHead>
                      <TableHead>Part</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Schedule Date</TableHead>
                      <TableHead>Days Late</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Ack Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastDueSchedules.map((s) => {
                      const days = daysLate(s.schedule_date)
                      return (
                        <TableRow key={s.id}>
                          <TableCell className="font-medium">{s.po_number}</TableCell>
                          <TableCell>{s.line_number}</TableCell>
                          <TableCell>{s.schedule_line_number}</TableCell>
                          <TableCell>{s.part_number}</TableCell>
                          <TableCell>{s.supplier_name}</TableCell>
                          <TableCell>{s.schedule_date}</TableCell>
                          <TableCell className={daysLateClass(days)}>{days}</TableCell>
                          <TableCell className="text-right">{s.schedule_qty}</TableCell>
                          <TableCell className="text-right">{s.balance_qty}</TableCell>
                          <TableCell>
                            {s.acknowledge_status ? (
                              <Badge variant="outline">{s.acknowledge_status}</Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Note:</strong> Expediting status is calculated based on required delivery dates.
          Lines within 7 days of due date are marked "At Risk". Lines past due are marked "Late".
          {loading && ' Loading latest data...'}
        </p>
      </div>
    </div>
  )
}
